import { gridActionsSlot } from 'components/owner-notes/guide/GuidePick'
import { GuideOwnersProvider } from 'components/owner-notes/guide/GuideOwnersProvider'
import LabSurfaces, { useLabSurfacesDocked } from 'components/product/LabSurfaces'
import {
  CatalogAnalyticsProvider,
  guideArticleSlug,
  type CatalogAnalyticsScope,
} from 'lib/analytics/catalog'
import { useDeclaredState } from 'lib/owner-notes/declared'
import NC from 'nc/NC'
import React, { useMemo } from 'react'
import { useAffilate } from 'shared-components/hooks/use-affilate'
import AltBestListTemplate from 'shared-components/templates/AltBestListTemplate'
import { DSAltBestListPageSG } from 'types.sg.ds'
import styles from './AltBestListTemplateSG.module.css'

interface AltBestListTemplateSGProps {
  page: DSAltBestListPageSG
}

const AltBestListBody: React.VFC<AltBestListTemplateSGProps> = ({ page }) => {
  useAffilate(page.productBars)
  // The mobile conversation bar yields while the LabCompare drawer / pill is docked (a pin, a
  // focus or a declared product) — not on every page with a strip.
  const labDocked = useLabSurfacesDocked()
  // The pick's action row on the "Our picks" cards (2026-10-17): Pin to compare · Measurements ·
  // Owners, the in-depth cards' row compact, found per card by ClearBuy id. (For one round the
  // card carried the pin alone, on its photo.)
  const cardFooterSlot = useMemo(() => gridActionsSlot(page.guidePicks), [page.guidePicks])
  return (
    <AltBestListTemplate
      page={page}
      nc={NC}
      cardFooterSlot={cardFooterSlot}
      className={styles.house}
      hideConversationBar={labDocked}
    />
  )
}

/**
 * AA's "new best list style" on SoundGuys: the shared `AltBestListTemplate` (expert card, trust
 * box, "Our picks" grid, TOC) under the same page-level state `BestListTemplate` mounts for the
 * owner-note blocks the mapper splices into the in-depth sections — the guide_page_* analytics
 * scope, the ONE declared product (#63) and the lab-compare surfaces. The layout has no sidebar,
 * so the desktop rail ask of the old style has no seat here.
 */
const AltBestListTemplateSG: React.VFC<AltBestListTemplateSGProps> = props => {
  const { page } = props
  const scope = useMemo<CatalogAnalyticsScope>(
    () => ({
      page_type: 'guide_page',
      ...(page.meta.categories[0]?.label ? { category: page.meta.categories[0].label } : {}),
      hub: 'best',
      scope: guideArticleSlug(page.pagePath),
    }),
    [page.meta.categories, page.pagePath]
  )
  const declared = useDeclaredState()
  return (
    <CatalogAnalyticsProvider value={scope}>
      <GuideOwnersProvider page={page.guideOwners}>
        <LabSurfaces declared={declared} mountOnPin>
          <AltBestListBody {...props} />
        </LabSurfaces>
      </GuideOwnersProvider>
    </CatalogAnalyticsProvider>
  )
}

export default AltBestListTemplateSG
